import React, { useEffect, useRef, useState } from "react";
import { loadKanopyTokenJs } from "utils/kanopyTokenJs";
import Label from "components/Label/Label";
import { PaymentFormSkeleton } from "shared/OnlineOrderingSkeletons";

interface KanopyPaymentFormProps {
    /** Kanopy merchant device GUID — from OnlineOrderSettings context */
    deviceGuid: string;
    /** Kanopy merchant API key — from OnlineOrderSettings context */
    apiKey: string;
    /** Called with an error message if Token.js fails to load */
    onLoadError?: (msg: string) => void;
}

/**
 * Renders the three div containers that Kanopy Token.js injects card fields into.
 * Styled to match the existing PaymentForm.tsx (Auth.net) layout.
 *
 * Token.js injects <input> elements into #ccNumber, #ccExpDate, #cvv with the class
 * specified by merch-style-class on the script tag ("kanopy-input" — styled below).
 */
const KanopyPaymentForm: React.FC<KanopyPaymentFormProps> = ({ deviceGuid, apiKey, onLoadError }) => {
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const loadedRef = useRef(false);

    const attemptLoad = () => {
        setLoading(true);
        setLoadError(null);
        loadKanopyTokenJs(deviceGuid, apiKey)
            .then(() => setLoading(false))
            .catch((e: Error) => {
                const msg = e.message ?? "Failed to load payment form. Please try again.";
                setLoadError(msg);
                setLoading(false);
                onLoadError?.(msg);
            });
    };

    useEffect(() => {
        if (loadedRef.current) return;
        loadedRef.current = true;
        attemptLoad();
    }, []);

    if (loadError) {
        return (
            <div className="text-red-500 text-sm mt-2">
                <p>{loadError}</p>
                <button
                    type="button"
                    onClick={() => { loadedRef.current = false; attemptLoad(); }}
                    className="mt-2 text-sm text-blue-500 hover:text-blue-400 underline"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="mt-6 mb-4 space-y-3 sm:space-y-5">
            {/* Inject global styles for Token.js inputs to match the project's Input component */}
            <style>{`
                #ccNumber input,
                #ccExpDate input,
                #cvv input {
                    display: block;
                    width: 100%;
                    height: 2.75rem;
                    padding: 0.75rem 1rem;
                    font-size: 0.875rem;
                    font-weight: 400;
                    line-height: 1.25rem;
                    border: 1px solid rgb(229 231 235);
                    border-radius: 1rem;
                    background-color: white;
                    outline: none;
                    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
                }
                #ccNumber input:focus,
                #ccExpDate input:focus,
                #cvv input:focus {
                    border-color: rgb(165 180 252);
                    box-shadow: 0 0 0 3px rgba(165, 180, 252, 0.5);
                }
                .dark #ccNumber input,
                .dark #ccExpDate input,
                .dark #cvv input {
                    background-color: rgb(30 41 59);
                    border-color: rgb(71 85 105);
                    color: rgb(241 245 249);
                }
                .dark #ccNumber input:focus,
                .dark #ccExpDate input:focus,
                .dark #cvv input:focus {
                    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.25);
                }
            `}</style>

            {loading && (
                <PaymentFormSkeleton className="mt-2" />
            )}

            {/* Credit card icon + label — matches PaymentForm.tsx header */}
            <label
                htmlFor="Credit-Card"
                className="flex items-center space-x-4 sm:space-x-6"
            >
                <div className="p-2.5 rounded-xl border-2 border-slate-200 dark:border-slate-600 text-slate-800 dark:text-slate-200">
                    <svg
                        className="w-6 h-6 sm:w-7 sm:h-7"
                        viewBox="0 0 24 24"
                        fill="none"
                    >
                        <path
                            d="M2 12.6101H19"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeMiterlimit="10"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <path
                            d="M19 10.28V17.43C18.97 20.28 18.19 21 15.22 21H5.78003C2.76003 21 2 20.2501 2 17.2701V10.28C2 7.58005 2.63 6.71005 5 6.57005C5.24 6.56005 5.50003 6.55005 5.78003 6.55005H15.22C18.24 6.55005 19 7.30005 19 10.28Z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <path
                            d="M22 6.73V13.72C22 16.42 21.37 17.29 19 17.43V10.28C19 7.3 18.24 6.55 15.22 6.55H5.78003C5.50003 6.55 5.24 6.56 5 6.57C5.03 3.72 5.81003 3 8.78003 3H18.22C21.24 3 22 3.75 22 6.73Z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <path
                            d="M5.25 17.8101H6.96997"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeMiterlimit="10"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <path
                            d="M9.10986 17.8101H12.5499"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeMiterlimit="10"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>
                <p className="font-medium text-slate-900 dark:text-slate-100">
                    Debit / Credit Card
                </p>
            </label>

            {/* Card Number — full width, matches PaymentForm max-w-lg */}
            <div className="max-w-lg scroll-mt-24">
                <Label className="text-sm">
                    <span className="text-red-500" aria-hidden>*</span> Card number
                    <span className="sr-only"> (required)</span>
                </Label>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    Secure field — loads below when ready.
                </p>
                <div id="ccNumber" className="mt-1.5 min-h-[44px]" tabIndex={-1} aria-label="Card number (hosted field)" />
            </div>

            {/* Expiration + CVV — side by side, matches PaymentForm layout */}
            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
                <div className="sm:w-2/3 scroll-mt-24">
                    <Label className="text-sm">
                        <span className="text-red-500" aria-hidden>*</span> Expiration (MM/YY)
                        <span className="sr-only"> (required)</span>
                    </Label>
                    <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                        Hosted secure field.
                    </p>
                    <div id="ccExpDate" className="mt-1.5 min-h-[44px]" tabIndex={-1} aria-label="Expiration date (hosted field)" />
                </div>
                <div className="flex-1 scroll-mt-24">
                    <Label className="text-sm">
                        <span className="text-red-500" aria-hidden>*</span> Security code (CVV)
                        <span className="sr-only"> (required)</span>
                    </Label>
                    <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                        Hosted secure field.
                    </p>
                    <div id="cvv" className="mt-1.5 min-h-[44px]" tabIndex={-1} aria-label="Security code (hosted field)" />
                </div>
            </div>

            {/* Hidden trigger button — Kanopy hooks into id="paymentTokenButton" */}
            <button
                id="paymentTokenButton"
                type="button"
                style={{ display: "none" }}
                aria-hidden="true"
            />
        </div>
    );
};

export default KanopyPaymentForm;
